import { z } from 'zod'
import type { schema as slideDeckSchema } from '../firestore/SlideDeck/schema'
import type { schema as slideSchema } from '../firestore/Slide/schema'
import type { schema as slideQuestionSchema } from '../firestore/SlideQuestion/schema'
import type { schema as slideDeckExhibitSchema } from '../firestore/SlideDeckExhibit/schema'
import type { schema as slideDeckRoleSchema } from '../firestore/SlideDeckRole/schema'
import type { schema as slideDeckMaterialSchema } from '../firestore/SlideDeckMaterial/schema'
import type { schema as slideCaptionSchema } from '../firestore/SlideCaption/schema'
import type { schema as slideRubricSchema } from '../firestore/SlideRubric/schema'

export type DocWithId<T> = { id: string; data: T }

export const slideDeckIncludeEnum = z.enum([
  'slides',
  'questions',
  'exhibits',
  'roles',
  'materials',
  'slideCaptions',
  'rubrics',
])

export type SlideDeckInclude = z.infer<typeof slideDeckIncludeEnum>

export type IncludeDataMap = {
  slides: DocWithId<z.infer<typeof slideSchema>>[]
  questions: DocWithId<z.infer<typeof slideQuestionSchema>>[]
  exhibits: DocWithId<z.infer<typeof slideDeckExhibitSchema>>[]
  roles: DocWithId<z.infer<typeof slideDeckRoleSchema>>[]
  materials: DocWithId<z.infer<typeof slideDeckMaterialSchema>>[]
  slideCaptions: DocWithId<z.infer<typeof slideCaptionSchema>>[]
  rubrics: DocWithId<z.infer<typeof slideRubricSchema>>[]
}

// Shared result type — used by both the callable function and client cubits
type ApiDoc = { id: string; data: Record<string, unknown> }

export type GetSlideDeckForAssignmentResult =
  | {
      success: true
      slideDeck: ApiDoc
      slides?: ApiDoc[]
      questions?: ApiDoc[]
      exhibits?: ApiDoc[]
      roles?: ApiDoc[]
      materials?: ApiDoc[]
      slideCaptions?: ApiDoc[]
      rubrics?: ApiDoc[]
    }
  | {
      success: false
      error: string
      errorCode: 'NOT_FOUND' | 'NOT_MEMBER' | 'ACCESS_DENIED' | 'INTERNAL_ERROR'
    }

export type SlideDeckAccessResult<T extends SlideDeckInclude[]> = {
  success: true
  slideDeck: DocWithId<z.infer<typeof slideDeckSchema>>
} & Pick<IncludeDataMap, T[number]>

export type SlideDeckAccessErrorCode =
  | 'NOT_FOUND'
  | 'NOT_MEMBER'
  | 'ACCESS_DENIED'
  | 'INTERNAL_ERROR'

export type SlideDeckAccessError = {
  success: false
  error: string
  errorCode: SlideDeckAccessErrorCode
}
